import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '@/assets/app/css/mypage.module.css';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';
import useAppStore from '@/shared/store/UseAppStore';
import ImgHeaderLevel from '@/assets/app/images/common/header/header-level.png';
import ImgHeaderStar from '@/assets/app/images/common/header/header-star.png';
import ImgHeaderCalendar from '@/assets/app/images/common/header/header-calendar.png';
import defaultPersonImage from '@/assets/app/images/common/default-person.svg';
import ImgArrow from '@/assets/app/images/common/header/arrow.png';
import IconLeaf from '@/assets/app/images/icon/icon-leaf.png';
import IconPen from '@/assets/app/images/icon/icon-Pen.png';
import { getUploadsUrl, openUpdateNicknameSheet, vAppAjax } from '@/shared/utils/CommonUtil';
import { motion } from 'framer-motion';
import useBottomSheetStore from '@/shared/store/UseBottomSheetStore';
import logger from '@/shared/utils/Logger';

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { setIsShowBottomNavigation, setIsShowProfileHeader } = useAppStore();
  const [data, setData] = useState<CompletedInfo>();
  const { member } = useAppAuthStore();

  const loadData = async () => {
    await vAppAjax(
      'get',
      `my/recent-achievement`,
      {},
      (response: AxiosResponse<CommonResult<CompletedInfo>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setData(response.data.result);
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  useEffect(() => {
    setIsShowBottomNavigation(true);
    setIsShowProfileHeader(false);
    loadData();
  }, []);

  return (
    <div className={`page-container page-container--gray ${styles.mypage}`}>
      <div
        className="relative font-bold text-right right-5 top-5"
        style={{ zIndex: 100 }}
        onClick={() => navigate('/mypage/settings')}
      >
        <span style={{ color: 'rgb(20, 161, 200)' }}>설정</span>
      </div>
      <div className={styles['mypage__profile-section']} style={{ color: '#000' }}>
        <div
          className="character-container relative"
          style={{ margin: 0, marginTop: 0, minHeight: 100 }}
        >
          <div className="relative inline-block">
            {/* 메인 캐릭터 이미지 */}
            <img
              src={
                member?.profileImageFilename
                  ? `${getUploadsUrl()}/character/${member.profileImageFilename}`
                  : defaultPersonImage
              }
              alt={member?.nickname}
              className="character-avatar block" // block을 주어 하단 미세 공백 제거
              style={{ border: 0 }}
            />

            {/* 2. 펜 아이콘: 이제 캐릭터 이미지의 우측 하단에 고정됨 */}
            <div
              className="absolute bottom-0 right-0 bg-white flex"
              style={{
                width: 20,
                height: 20,
                border: '1px #EBEBEB solid',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
              }}
              onClick={() => navigate('character')}
            >
              <img src={IconPen} alt="edit" />
            </div>
          </div>
        </div>

        <div className={styles['mypage__profile-info']}>
          <h2 className={styles['mypage__username']} style={{ fontSize: 16 }}>
            {member?.nickname}
            <img
              src={ImgArrow}
              onClick={openUpdateNicknameSheet}
              style={{
                marginLeft: 4,
                display: 'inline-block',
                width: 12,
                height: 12,
                verticalAlign: 'baseline',
              }}
            />
          </h2>
          <p className={styles['mypage__join-date']} style={{ fontSize: 12, color: '#4B4B4B' }}>
            가입일: {new Date(member?.createdAt || '').toLocaleDateString('ko-KR')}
          </p>
        </div>

        <div className="w-full px-[25px] mt-[15px]">
          <div className="flex justify-between gap-[10px]">
            <div className="flex-1 flex flex-col items-center justify-center h-[96px] border border-[#E5E5E5] rounded-[14px] bg-white">
              <img src={ImgHeaderLevel} alt="level" className="mb-1" />
              <span className="text-[16px] font-extrabold">Lv.{member?.level}</span>
              <span className="text-[11px] font-normal text-[#8D8D8D]">나의 레벨</span>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center h-[96px] border border-[#E5E5E5] rounded-[14px] bg-white">
              <img src={ImgHeaderStar} alt="points" className="mb-1" />
              <span className="text-[16px] font-extrabold">{member?.totalPoints}</span>
              <span className="text-[11px] font-normal text-[#8D8D8D]">별포인트</span>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center h-[96px] border border-[#E5E5E5] rounded-[14px] bg-white">
              <img src={ImgHeaderCalendar} alt="calendar" className="mb-1" />
              <div className="text-[16px] font-extrabold flex items-baseline">
                {member?.consecutiveDays}
                <span className="text-[12px] font-normal ml-0.5" style={{ color: '#E1464D' }}>
                  일차
                </span>
              </div>
              <span className="text-[11px] font-normal text-[#8D8D8D]">연속학습</span>
            </div>
          </div>
        </div>

        <div
          className={`card ${styles['mypage__encouragement-card']}`}
          style={{
            height: 98,
            border: '3px solid #50BBEC',
            boxShadow: 'none',
            backgroundColor: '#F6FAFF',
            margin: '15px 25px',
            position: 'relative',
            padding: 12,
          }}
        >
          <div
            className={styles['mypage__card-icon']}
            style={{ position: 'absolute', right: 20, bottom: 10 }}
          >
            <img src={IconLeaf} />
          </div>
          <div className={styles['mypage__card-content']}>
            <p className="font-bold">
              레벨이 오르면
              <br /> 씨앗별 포인트가 쑤욱 자라나요
            </p>
          </div>
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              onClick={() => navigate('level')}
              style={{
                backgroundColor: '#0082CA',
                width: 60,
                height: 18,
                borderRadius: 6,
                color: '#fff',
                fontSize: 11,
                fontWeight: 400,
              }}
            >
              크게보기
            </button>
          </div>
        </div>
      </div>

      <div style={{ width: '100%', height: 14, backgroundColor: '#F2F2F2' }}></div>

      <div className="section" style={{ margin: '15px 25px' }}>
        <div className="section-header">
          <h3 className="section-header__title" style={{ fontSize: 18, fontWeight: 800 }}>
            나의 업적
          </h3>
          {/* <button className="section-header__action" onClick={() => navigate('/achievements')}> */}
          <button
            className="section-header__action"
            style={{ color: '#14A1C8', fontSize: 12 }}
            onClick={() => navigate('/mypage/achievements')}
          >
            전체보기
          </button>
        </div>

        {data?.personName && (
          <div className="flex items-center justify-center gap-3 mb-2 mt-4">
            <span className="px-3 py-0.5 border border-cyan-400 text-[#14A1C8] text-xs font-bold rounded-full">
              최근
            </span>
            <span className="text-sm color-[#4B4B4B] font-medium">
              {data.personName} · {data.chapterTitle} · {data.categoryName}
            </span>
          </div>
        )}

        <div className="flex items-center justify-center border border-[#e5e5e5] rounded-[14px] w-100p h-[67px] mt-3 mx-auto">
          <div className="flex-1 text-center">
            <div className="text-[16px] font-extrabold text-[#4b4b4b]">
              {data?.completedPerson || 0}
            </div>
            <div className="text-[11px] text-[#8d8d8d] mt-1">완료한 인물</div>
          </div>

          <div className="w-[0.5px] h-[42px] bg-[#efefef]"></div>

          <div className="flex-1 text-center">
            <div className="text-[16px] font-extrabold text-[#4b4b4b]">
              {data?.completedEra || 0}
            </div>
            <div className="text-[11px] text-[#8d8d8d] mt-1">완료한 시대</div>
          </div>
        </div>

        {data?.achievements.map((item, idx) => (
          <div key={idx} className="bg-white py-4 flex items-center gap-4 ">
            <div
              className={`w-20 px-2 py-1 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0`}
              style={{ background: `${item.themeColor}` }}
            >
              {item.categoryName.replace('시대', '')}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-[16px] bg-[#DDDDDD] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(item.completedChapters / item.totalChapters) * 100}%` }}
                    className="h-full rounded-lg"
                    style={{ backgroundColor: '#14A1C8' }}
                  />
                </div>
                <span className="text-xs font-bold" style={{ color: '#14A1C8' }}>
                  {item.completedChapters}/{item.totalChapters}
                </span>
              </div>
            </div>
          </div>
        ))}

        {/* <div className={styles['mypage__current-stats']}>
          <div className="badge badge--primary">
            <span>퀴즈</span>
            <span>{member?.level}</span>
          </div>
          <div className="text-secondary">칭찬값 · 꾸준히 · 고급서비</div>
        </div>

        <div className="stats-grid mb-lg">
          <div className="card text-center">
            <div className="stat-item__value">
              {{achievements.filter(a => a.current === a.total).length} 
            </div>
            <div className="text-secondary">하루로 연속</div>
          </div>
          <div className="card text-center">
            <div className="stat-item__value">1</div>
            <div className="text-secondary">문항로 사례</div>
          </div>
        </div>

        <div className={`card ${styles['mypage__daily-progress']}`}>
          <div className={styles['mypage__progress-header']}>
            <span className="font-semibold">코칭</span>
            <div className="progress-bar progress-bar--thick" style={{ flex: 1 }}>
              <div
                className="progress-bar__fill progress-bar__fill--primary"
                style={{ width: '65%' }}
              />
            </div>
            <span className="text-primary-color font-semibold">13/14</span>
          </div>
        </div>

        <div className={styles['mypage__achievements-list']}>
          {achievements
            .slice(0, 5)
            .map((achievement, index) => renderAchievementItem(achievement, index))}
        </div> */}
      </div>
    </div>
  );
};

export default MyPage;
